import React, { useState } from 'react';
import { authService } from '../../services/auth';
import { theme } from '../../styles/theme';
import Button from '../ui/Button';
import Card from '../ui/Card';

const DashboardPassword = ({ showSuccess, showError, showConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.newPassword.length < 6) {
      showError('New password must be at least 6 characters long', 'Invalid Password');
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      showError('New passwords do not match', 'Password Mismatch');
      return;
    }
    
    if (formData.currentPassword === formData.newPassword) {
      showError('New password must be different from current password', 'Invalid Password');
      return;
    }
    
    await showConfirm({
      title: 'Change Password',
      message: 'Are you sure you want to change your password? You will remain logged in.',
      type: 'warning',
      confirmText: 'Change Password',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          setLoading(true);
          await authService.changePassword(formData.currentPassword, formData.newPassword);
          showSuccess('Your password has been changed successfully!', 'Password Changed');
          
          // Clear form
          setFormData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
        } catch (err) {
          let errorMsg = 'Failed to change password';
          
          // Handle specific Firebase errors
          if (err.code === 'auth/wrong-password') {
            errorMsg = 'Current password is incorrect';
          } else if (err.code === 'auth/weak-password') {
            errorMsg = 'New password is too weak. Please choose a stronger password';
          } else if (err.code === 'auth/requires-recent-login') {
            errorMsg = 'For security reasons, please log out and log back in before changing your password';
          } else if (err.message) {
            errorMsg = err.message;
          }
          
          showError(errorMsg, 'Password Change Failed');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const styles = {
    container: {
      width: '100%',
    },
    header: {
      marginBottom: theme.spacing.xl,
    },
    title: {
      fontSize: theme.typography.fontSize['3xl'],
      fontFamily: theme.typography.fontFamily.primary,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      margin: 0,
      marginBottom: theme.spacing.xs,
    },
    subtitle: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textLight,
      margin: 0,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    formContainer: {
      maxWidth: '600px',
      margin: '0 auto',
    },
    formSection: {
      padding: theme.spacing.xl,
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.sm,
      marginBottom: theme.spacing.lg,
    },
    label: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      fontFamily: theme.typography.fontFamily.secondary,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.xs,
    },
    labelHint: {
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.normal,
      color: theme.colors.textLight,
      fontStyle: 'italic',
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    input: {
      width: '100%',
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      paddingRight: '48px',
      fontSize: theme.typography.fontSize.base,
      fontFamily: theme.typography.fontFamily.secondary,
      border: `2px solid ${theme.colors.borderLight}`,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      color: theme.colors.text,
      transition: `all ${theme.transitions.base}`,
      outline: 'none',
    },
    toggleButton: {
      position: 'absolute',
      right: '12px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.colors.textLight,
      transition: `color ${theme.transitions.base}`,
    },
    formActions: {
      display: 'flex',
      gap: theme.spacing.md,
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
      padding: theme.spacing.xl,
      backgroundColor: '#F9F9F9',
      borderTop: `2px solid ${theme.colors.borderLight}`,
    },
    securityNote: {
      backgroundColor: '#EFF6FF',
      border: `2px solid #3B82F6`,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.lg,
      marginBottom: theme.spacing.xl,
      display: 'flex',
      gap: theme.spacing.md,
      alignItems: 'flex-start',
    },
    securityIcon: {
      color: '#3B82F6',
      flexShrink: 0,
      marginTop: '2px',
    },
    securityContent: {
      flex: 1,
    },
    securityTitle: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      color: '#1E40AF',
      margin: `0 0 ${theme.spacing.xs} 0`,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    securityText: {
      fontSize: theme.typography.fontSize.sm,
      color: '#1E40AF',
      margin: 0,
      fontFamily: theme.typography.fontFamily.secondary,
      lineHeight: '1.5',
    },
  };

  const responsiveStyles = `
    .password-input:focus {
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 4px rgba(204, 85, 0, 0.1);
      transform: translateY(-1px);
    }

    .password-toggle-btn:hover {
      color: ${theme.colors.primary};
    }

    @media (max-width: 768px) {
      .password-header h2 {
        font-size: ${theme.typography.fontSize['2xl']};
      }

      .password-form-section,
      .password-form-actions {
        padding: ${theme.spacing.lg};
      }
    }

    @media (max-width: 480px) {
      .password-form-actions {
        flex-direction: column-reverse;
      }

      .password-form-actions button {
        width: 100%;
      }

      .password-form-section {
        padding: ${theme.spacing.md};
      }
    }
  `;

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={styles.container}>
        <div style={styles.header} className="password-header">
          <h2 style={styles.title}>Change Password</h2>
          <p style={styles.subtitle}>Update your account password</p>
        </div>

        <div style={styles.formContainer}>
          <div style={styles.securityNote}>
            <div style={styles.securityIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 0C8.9 0 8 0.9 8 2V3H4C2.9 3 2 3.9 2 5V18C2 19.1 2.9 20 4 20H16C17.1 20 18 19.1 18 18V5C18 3.9 17.1 3 16 3H12V2C12 0.9 11.1 0 10 0ZM10 2C10.6 2 11 2.4 11 3V4H9V3C9 2.4 9.4 2 10 2ZM10 8C11.1 8 12 8.9 12 10C12 10.7 11.6 11.4 11 11.7V14C11 14.6 10.6 15 10 15C9.4 15 9 14.6 9 14V11.7C8.4 11.4 8 10.7 8 10C8 8.9 8.9 8 10 8Z" fill="currentColor"/>
              </svg>
            </div>
            <div style={styles.securityContent}>
              <h3 style={styles.securityTitle}>Security Tips</h3>
              <p style={styles.securityText}>
                Choose a strong password with at least 6 characters. For better security, use a mix of uppercase and lowercase letters, numbers, and special characters.
              </p>
            </div>
          </div>

          <Card variant="parchment" padding="none">
            <form onSubmit={handleSubmit}>
              <div style={styles.formSection} className="password-form-section">
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Current Password *
                    <span style={styles.labelHint}>Enter your current password to verify your identity</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <input 
                      type={showPasswords.current ? "text" : "password"}
                      value={formData.currentPassword} 
                      onChange={(e) => setFormData({...formData, currentPassword: e.target.value})} 
                      required 
                      style={styles.input}
                      className="password-input"
                      placeholder="Enter current password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      style={styles.toggleButton}
                      className="password-toggle-btn"
                      aria-label={showPasswords.current ? "Hide password" : "Show password"}
                    >
                      {showPasswords.current ? (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 4C5 4 1.73 7.11 1 10C1.73 12.89 5 16 10 16C15 16 18.27 12.89 19 10C18.27 7.11 15 4 10 4ZM10 14C7.79 14 6 12.21 6 10C6 7.79 7.79 6 10 6C12.21 6 14 7.79 14 10C14 12.21 12.21 14 10 14ZM10 8C8.9 8 8 8.9 8 10C8 11.1 8.9 12 10 12C11.1 12 12 11.1 12 10C12 8.9 11.1 8 10 8Z" fill="currentColor"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 4C5 4 1.73 7.11 1 10C1.73 12.89 5 16 10 16C15 16 18.27 12.89 19 10C18.27 7.11 15 4 10 4ZM10 14C7.79 14 6 12.21 6 10C6 7.79 7.79 6 10 6C12.21 6 14 7.79 14 10C14 12.21 12.21 14 10 14Z" fill="currentColor"/>
                          <line x1="2" y1="2" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    New Password *
                    <span style={styles.labelHint}>Must be at least 6 characters long</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <input 
                      type={showPasswords.new ? "text" : "password"}
                      value={formData.newPassword} 
                      onChange={(e) => setFormData({...formData, newPassword: e.target.value})} 
                      required 
                      minLength={6}
                      style={styles.input}
                      className="password-input"
                      placeholder="Enter new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      style={styles.toggleButton}
                      className="password-toggle-btn"
                      aria-label={showPasswords.new ? "Hide password" : "Show password"}
                    >
                      {showPasswords.new ? (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 4C5 4 1.73 7.11 1 10C1.73 12.89 5 16 10 16C15 16 18.27 12.89 19 10C18.27 7.11 15 4 10 4ZM10 14C7.79 14 6 12.21 6 10C6 7.79 7.79 6 10 6C12.21 6 14 7.79 14 10C14 12.21 12.21 14 10 14ZM10 8C8.9 8 8 8.9 8 10C8 11.1 8.9 12 10 12C11.1 12 12 11.1 12 10C12 8.9 11.1 8 10 8Z" fill="currentColor"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 4C5 4 1.73 7.11 1 10C1.73 12.89 5 16 10 16C15 16 18.27 12.89 19 10C18.27 7.11 15 4 10 4ZM10 14C7.79 14 6 12.21 6 10C6 7.79 7.79 6 10 6C12.21 6 14 7.79 14 10C14 12.21 12.21 14 10 14Z" fill="currentColor"/>
                          <line x1="2" y1="2" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Confirm New Password *
                    <span style={styles.labelHint}>Re-enter your new password</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <input 
                      type={showPasswords.confirm ? "text" : "password"}
                      value={formData.confirmPassword} 
                      onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} 
                      required 
                      minLength={6}
                      style={styles.input}
                      className="password-input"
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      style={styles.toggleButton}
                      className="password-toggle-btn"
                      aria-label={showPasswords.confirm ? "Hide password" : "Show password"}
                    >
                      {showPasswords.confirm ? (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 4C5 4 1.73 7.11 1 10C1.73 12.89 5 16 10 16C15 16 18.27 12.89 19 10C18.27 7.11 15 4 10 4ZM10 14C7.79 14 6 12.21 6 10C6 7.79 7.79 6 10 6C12.21 6 14 7.79 14 10C14 12.21 12.21 14 10 14ZM10 8C8.9 8 8 8.9 8 10C8 11.1 8.9 12 10 12C11.1 12 12 11.1 12 10C12 8.9 11.1 8 10 8Z" fill="currentColor"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 4C5 4 1.73 7.11 1 10C1.73 12.89 5 16 10 16C15 16 18.27 12.89 19 10C18.27 7.11 15 4 10 4ZM10 14C7.79 14 6 12.21 6 10C6 7.79 7.79 6 10 6C12.21 6 14 7.79 14 10C14 12.21 12.21 14 10 14Z" fill="currentColor"/>
                          <line x1="2" y1="2" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div style={styles.formActions} className="password-form-actions">
                <Button 
                  variant="outline" 
                  onClick={() => setFormData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                  })} 
                  type="button" 
                  size="lg"
                >
                  Clear
                </Button>
                <Button type="submit" disabled={loading} size="lg">
                  {loading ? 'Changing Password...' : 'Change Password'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </>
  );
};

export default DashboardPassword;
